import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createJudgeSession, saveJudgeScore, saveJudgeWildcard } from '@/api/battles';
import { BattleResults, CATEGORY_LABELS, entrySubtitle } from './BattleResults';

const POLL_MS = 3500;
const CATEGORY_KEYS = ['creativity', 'emotion', 'transitions', 'clarity'];
const clampScore = (value) => Math.min(10, Math.max(6, Math.round((Number(value) || 6) * 10) / 10));
const band = (value) => value < 7.5 ? 'Developing' : value < 9 ? 'Strong' : 'Exceptional';
const freshScore = () => ({ creativity: 8, emotion: 8, transitions: 8, clarity: 8, comment: '' });

const SECRET_KEY = 'ba-judge-secret';

// The secret arrives in the link's #fragment. Read it once, strip it from the address bar, and
// keep it for this tab only (sessionStorage) so a phone reload does not lock the judge out.
// Never overwrite a known secret with an empty one: React dev mode runs effects twice, and the
// second run sees the hash already stripped.
export function readJudgeSecret() {
  const fromHash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
  if (window.location.hash) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  try {
    if (fromHash) { window.sessionStorage.setItem(SECRET_KEY, fromHash); return fromHash; }
    return window.sessionStorage.getItem(SECRET_KEY) || '';
  } catch (storageError) {
    return fromHash;
  }
}

export function JudgePage() {
  const secretRef = useRef('');
  const priorHostEntry = useRef('');
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [score, setScore] = useState(freshScore);
  const [pending, setPending] = useState('');
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    if (!secretRef.current) return;
    try {
      const next = await createJudgeSession(secretRef.current);
      setData(next); setError('');
      const hostEntry = next.battle.current_entry_id || next.queue[0]?.id || '';
      setSelectedId((current) => (!current || (hostEntry && hostEntry !== priorHostEntry.current)) ? hostEntry : current);
      priorHostEntry.current = hostEntry;
    } catch (requestError) { setError(requestError.status === 401 ? 'This judge link has expired or was replaced. Ask the host for a new link.' : requestError.message); }
  }, []);
  useEffect(() => {
    if (!secretRef.current) secretRef.current = readJudgeSecret();
    if (!secretRef.current) { setError('This judge link is missing. Ask the host for a new link.'); return undefined; }
    load();
    return undefined;
  }, [load]);
  useEffect(() => {
    if (data?.battle.status !== 'live') return undefined;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [data?.battle.status, load]);

  const entry = data?.queue?.find((item) => item.id === selectedId) || null;
  const savedScoreSignature = JSON.stringify(entry?.my_scorecard || null);
  useEffect(() => {
    const card = JSON.parse(savedScoreSignature);
    setScore(card ? {
      creativity: card.creativity, emotion: card.emotion,
      transitions: card.transitions, clarity: card.clarity, comment: card.comment || '',
    } : freshScore());
  }, [entry?.id, savedScoreSignature]);
  const average = useMemo(() => CATEGORY_KEYS.reduce((total, key) => total + Number(score[key]), 0) / CATEGORY_KEYS.length, [score]);
  const updateScore = (key, value) => setScore((current) => ({ ...current, [key]: clampScore(value) }));
  const save = async () => {
    if (!entry) return;
    setPending('score'); setError('');
    try {
      const next = await saveJudgeScore(secretRef.current, entry.id, score);
      setData(next);
    } catch (requestError) { setError(requestError.message); }
    finally { setPending(''); }
  };
  const wildcard = async (entryId) => {
    setPending('wildcard'); setError('');
    try { setData(await saveJudgeWildcard(secretRef.current, data.wildcard_entry_id === entryId ? null : entryId)); }
    catch (requestError) { setError(requestError.message); }
    finally { setPending(''); }
  };

  if (!data) return <main className="judge-page judge-message" data-skin="playground"><div className="judge-brand">LET THE BEAT BUILD</div><h1>Judge link</h1><p role={error ? 'alert' : 'status'}>{error || 'Opening your scorecard...'}</p></main>;
  const revealed = data.battle.status === 'revealed';
  return <main className="judge-page" data-skin="playground">
    <header><div className="judge-brand">LET THE BEAT BUILD</div><span>{data.judge.name}</span><h1>{data.battle.name}</h1></header>
    {error && <p className="judge-error" role="alert">{error}</p>}
    {revealed ? <BattleResults results={data.results} compact /> : <>
      <nav className="judge-queue" aria-label="Entry queue">{data.queue.map((item) => <button key={item.id} className={`${item.id === data.battle.current_entry_id ? 'host-current' : ''}${item.id === selectedId ? ' selected' : ''}`} aria-current={item.id === selectedId ? 'true' : undefined} onClick={() => setSelectedId(item.id)}>
        <b>#{item.position}</b><span>{item.title}<small>{item.team.name} · {item.selector_name}</small></span><strong aria-label={item.done ? 'Scored' : 'Not scored'}>{item.done ? '✓' : '○'}</strong>
      </button>)}</nav>
      {entry && <section className="judge-scorecard" aria-labelledby="scorecard-title">
        <div className="judge-now"><span>{entry.id === data.battle.current_entry_id ? 'Now playing' : `Entry ${entry.position}`}</span><h2 id="scorecard-title">{entrySubtitle(entry)}</h2><p>{entry.team.name} · {entry.selector_name}</p></div>
        {CATEGORY_KEYS.map((key) => <div className="judge-score-row" key={key}>
          <div><label htmlFor={`score-${key}`}>{CATEGORY_LABELS[key]}</label><p>{data.rubric[key]}</p><strong>{band(score[key])}</strong></div>
          <div className="judge-stepper"><button aria-label={`Decrease ${CATEGORY_LABELS[key]}`} onClick={() => updateScore(key, score[key] - 0.1)}>−</button><input id={`score-${key}`} type="number" inputMode="decimal" min="6" max="10" step="0.1" value={Number(score[key]).toFixed(1)} onChange={(event) => updateScore(key, event.target.value)} /><button aria-label={`Increase ${CATEGORY_LABELS[key]}`} onClick={() => updateScore(key, score[key] + 0.1)}>+</button></div>
        </div>)}
        <div className="judge-average" aria-live="polite"><span>My live average</span><strong>{average.toFixed(2)}</strong><b>{band(average)}</b></div>
        <label className="judge-comment">Optional comment<textarea maxLength="500" value={score.comment} onChange={(event) => setScore((current) => ({ ...current, comment: event.target.value }))} /></label>
        <button className="judge-submit" disabled={pending === 'score'} onClick={save}>{pending === 'score' ? 'Saving...' : entry.done ? 'Update score' : 'Submit score'}</button>
        {data.settings?.tiebreak_wildcard && <><button className={`judge-wildcard${data.wildcard_entry_id === entry.id ? ' active' : ''}`} aria-pressed={data.wildcard_entry_id === entry.id} disabled={pending === 'wildcard'} onClick={() => wildcard(entry.id)}>{data.wildcard_entry_id === entry.id ? 'Wild card picked' : data.wildcard_entry_id ? 'Move wild card here' : 'Give this entry my wild card'}</button>
        <p className="judge-wildcard-help">You have one wild card for this battle. You can move it until results are revealed.</p></>}
      </section>}
    </>}
  </main>;
}

export { clampScore };
export default JudgePage;
